import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Card, Grid, styled, Badge, Chip, Avatar, Box, Link, Tooltip } from '@mui/material';
import React, { forwardRef, useRef } from 'react';
import { getJobByJobCode } from 'wisestep-api';
import {
  WsColors,
  WsTypoColors,
  WsTypography,
  timeSince,
  ActiveJob,
  WsTypoVariants,
  formateDateForNewSubmission,
} from 'wisestep-base';

import { openUrlInNewTab } from '../../../../../../src/utils/openUrlInNewTab';
import { useWorkspaceContext } from '../../../../../context';

type ChipType = {
  textColor: string;
  bgColor?: string;
  fontWeight?: number;
  mt?: string | number;
};
export const StyledChip = styled(Chip)(
  ({ textColor, bgColor = WsColors.TheBlues200, fontWeight = 800, mt = '0px' }: ChipType) => ({
    color: textColor,
    borderRadius: 4,
    fontSize: '14px',
    marginRight: 5,
    marginTop: mt,
    background: bgColor,
    fontWeight: fontWeight,
    height: '25px',
    paddingLeft: 0,
    paddingRight: 0,
  })
);

const Ribbon = styled(Box)({
  position: 'absolute',
  bottom: '30px',
  right: '30px',
  backgroundColor: '#00B960',
  color: 'white',
  padding: '4px 35px',
  fontWeight: 700,
  transform: 'translate(50%, 50%) rotate(-45deg)',
  zIndex: 1,
  fontSize: '13px',
});
export interface JobCardProps {
  title: string;
  jobCode: string;
  candidateStatus?: string;
  recruiterActions?: string[];
  candidateStatusTags?: string[];
  lastUpdated: string;
  onClick?: () => void;
  isSelected: boolean;
  isClosed?: boolean;
  updatedText?: string;
  isMappings?: boolean;
  joinedCandidate?: string;
  currentRTRStatus?: string;
}

const CardWithRef = forwardRef<HTMLDivElement, any>((props, ref) => <Card ref={ref} {...props} />);
CardWithRef.displayName = 'CardWithRef';

const JobCard = ({
  title,
  jobCode,
  candidateStatus = 'Applied',
  recruiterActions = ['action'],
  candidateStatusTags = ['result'],
  lastUpdated,
  currentRTRStatus,
  onClick = () => {},
  isSelected,
  isClosed = false,
  updatedText = 'lastUpdated :',
  joinedCandidate,
}: JobCardProps) => {
  const workspace = useWorkspaceContext();
  const cardRef = useRef<HTMLDivElement>(null);
  const handleGoBack = async () => {
    const jobResponse = await getJobByJobCode(jobCode);
    openUrlInNewTab(`/jobs-crm/${jobResponse.data.id}/false/0/pipeline/`);
  };

  let cardWidth: string;
  try {
    cardWidth = window.getComputedStyle(cardRef?.current).width.split('.')[0];
  } catch {
    console.log('Null element reference');
  }

  return (
    <Badge
      badgeContent={
        <Avatar sx={{ bgcolor: WsColors.TheBlues500, width: 24, height: 24 }}>
          <CheckIcon fontSize="small" />
        </Avatar>
      }
      invisible={!isSelected}
      sx={{ width: '100%', font: '8px' }}
    >
      <CardWithRef
        sx={{
          position: 'relative',
          width: '100%',
          border: 'solid 1px',
          borderColor: isSelected ? WsColors.TheBlues200 : WsColors.BorderGrey,
        }}
        onClick={onClick}
        ref={cardRef}
      >
        <Grid
          container
          py={2}
          px={1.5}
          sx={{
            backgroundColor: isSelected ? WsColors.TheBlue75 : WsColors.White,
            width: '100%',
            position: 'relative',
          }}
          rowSpacing={2}
        >
          {candidateStatusTags.includes('uploaded') ? (
            <Grid item xs={4} display="flex" justifyContent="flex-end">
              <StyledChip label={`uploaded`} textColor={WsColors.TheBlues700} />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} gap={0.5} alignItems="center">
                <Grid container sx={{ width: '100%' }} wrap="nowrap" justifyContent="space-between">
                  <Box display="flex" alignItems="start" columnGap={0.5} sx={{ mr: 2 }}>
                    <ActiveJob
                      color={isClosed ? WsColors.TertiaryShades500 : WsColors.TheBlues400}
                      color2={isClosed ? WsColors.TertiaryShades600 : WsColors.TheBlues500}
                      height={20}
                      width={20}
                    />
                    <Box>
                      <WsTypography
                        color={WsTypoColors.GREYTEXT}
                        bold={true}
                        onClick={handleGoBack}
                        cursor="pointer"
                      >
                        {title}
                      </WsTypography>
                      <WsTypography
                        color={WsTypoColors.SENARY}
                        variant={WsTypoVariants.CAPTION}
                        onClick={handleGoBack}
                        cursor="pointer"
                      >
                        {jobCode}
                      </WsTypography>
                    </Box>
                    <Link display="flex" onClick={handleGoBack} component="button">
                      <OpenInNewIcon color="primary" fontSize="small" />
                    </Link>
                  </Box>
                  <StyledChip
                    label={candidateStatus ?? `Applied`}
                    textColor={WsColors.TheBlues700}
                  />
                </Grid>
                <>
                  {workspace.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' ? (
                    <>
                      {recruiterActions
                        ?.slice(0, cardWidth > '350' ? 5 : 2)
                        .map((action: string, index: number) => (
                          <>
                            <Grid display="flex">
                              <StyledChip
                                key={index}
                                label={action}
                                textColor={WsColors.White}
                                bgColor={WsColors.BackGroundYellow}
                              />
                            </Grid>
                            <>
                              {currentRTRStatus?.includes('RTR') && !action?.includes('RTR') && (
                                <>
                                  <Box mt={2} />
                                  <Grid display="flex">
                                    <StyledChip
                                      label={currentRTRStatus}
                                      textColor={WsColors.White}
                                      bgColor={WsColors.BackGroundYellow}
                                    />
                                  </Grid>
                                </>
                              )}
                            </>
                          </>
                        ))}
                    </>
                  ) : (
                    <>
                      {recruiterActions
                        ?.slice(0, cardWidth > '350' ? 5 : 2)
                        .map((action: string, index: number) => (
                          <Grid display="flex" key={index}>
                            <StyledChip
                              label={action}
                              textColor={WsColors.White}
                              bgColor={WsColors.BackGroundYellow}
                            />
                          </Grid>
                        ))}
                    </>
                  )}
                  {/* {candidateStatusTags?.map((action: string, index: number) => (
                    <StyledChip key={index} label={action} textColor={WsColors.White} />
                  ))} */}
                </>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            {candidateStatusTags.includes('uploaded') ? (
              <WsTypography color={WsTypoColors.GREYTEXT} variant={WsTypoVariants.CAPTION}>
                {`Uploaded : ${timeSince(lastUpdated)}`}
              </WsTypography>
            ) : (
              <Tooltip title={formateDateForNewSubmission(lastUpdated)} placement="bottom">
                <span>
                  <WsTypography color={WsTypoColors.GREYTEXT} variant={WsTypoVariants.CAPTION}>
                    {`${updatedText}  ${timeSince(lastUpdated)}`}
                  </WsTypography>
                </span>
              </Tooltip>
            )}
          </Grid>
          {joinedCandidate === 'Joined' && <Ribbon>Joined</Ribbon>}
        </Grid>
      </CardWithRef>
    </Badge>
  );
};

export default JobCard;
